<template>
  <Loader :loading="httpSend">
    <!-- Sem sincronização / sem dados -->
    <div v-if="!sync" class="mt-3">
      <div class="alert alert-warning border-0 shadow-sm d-flex align-items-center" role="alert">
        <i class="mdi mdi-alert-outline font-size-22 mr-3 text-warning"></i>
        <div>
          <h6 class="alert-heading font-weight-bold mb-1">Apuração Não Realizada</h6>
          <p class="mb-0 text-muted">Ainda não foi realizada apuração para o período informado.</p>
        </div>
      </div>
    </div>

    <!-- Com dados de apuração -->
    <div v-else class="mt-2">
      <!-- Painel Resumo Superior -->
      <div class="row mb-3">
        <div class="col-12">
          <div class="card border-0 shadow-sm status-banner" :class="totalDivergencias > 0 ? 'status-danger' : 'status-success'">
            <div class="card-body p-3">
              <div class="d-flex flex-wrap align-items-center justify-content-between">
                <div class="d-flex align-items-center">
                  <div class="avatar-sm mr-3">
                    <span class="avatar-title rounded-circle font-size-20" :class="totalDivergencias > 0 ? 'bg-soft-danger text-danger' : 'bg-soft-success text-success'">
                      <i :class="totalDivergencias > 0 ? 'bx bx-error-circle' : 'bx bx-check-double'"></i>
                    </span>
                  </div>
                  <div>
                    <h5 class="mb-1 font-weight-bold" :class="totalDivergencias > 0 ? 'text-danger' : 'text-success'">
                      {{ totalDivergencias > 0 ? `${totalDivergencias} ${totalDivergencias === 1 ? 'Divergência Encontrada' : 'Divergências Encontradas'}` : 'Sem Divergências' }}
                    </h5>
                    <p class="text-muted font-size-13 mb-0">
                      Competência: <strong>{{ competencia.slice(4, 6) | monthNames }} de {{ competencia.slice(0, 4) }}</strong>
                    </p>
                  </div>
                </div>

                <div class="d-flex flex-wrap align-items-center gap-2 mt-2 mt-sm-0">
                  <span class="badge badge-pill font-size-12 px-3 py-2 badge-soft-info mr-2">
                    <i class="bx bx-list-check mr-1"></i> {{ listaAnalises.length }} Verificações
                  </span>
                  <span class="badge badge-pill font-size-12 px-3 py-2 badge-soft-success mr-2">
                    <i class="bx bx-check mr-1"></i> {{ totalConformes }} Conformes
                  </span>
                  <span
                    v-if="totalDivergencias > 0"
                    class="badge badge-pill font-size-12 px-3 py-2 badge-soft-danger"
                  >
                    <i class="bx bx-x mr-1"></i> {{ totalDivergencias }} Divergentes
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Quando NÃO HÁ nenhuma divergência -->
      <div v-if="totalDivergencias === 0" class="row">
        <div class="col-sm-12">
          <div class="card border-0 shadow-sm text-center py-5">
            <div class="card-body">
              <div class="avatar-md mx-auto">
                <span class="avatar-title rounded-circle bg-soft-success text-success font-size-28">
                  <i class="bx bx-check-shield"></i>
                </span>
              </div>
              <h4 class="text-success font-weight-bold">Tudo Certo! Nenhuma Divergência Encontrada</h4>
              <p class="text-muted mx-auto" style="max-width: 500px;">
                Os valores declarados no DAS-D estão consistentes com os documentos fiscais emitidos (NFS-e) para a competência de
                <strong>{{ competencia.slice(4, 6) | monthNames }} de {{ competencia.slice(0, 4) }}</strong>.
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Quando HÁ divergências -->
      <div v-else class="row">
        <!-- Coluna Esquerda: Cards de Divergências Detalhados -->
        <div class="col-lg-7">
          <div
            v-for="item in listaDivergentes"
            :key="item.key"
            :id="'card-' + item.key"
            class="card border-0 shadow-sm card-divergencia"
          >
            <!-- Cabeçalho do Card -->
            <div class="card-header bg-white border-bottom py-3 d-flex align-items-center justify-content-between">
              <div class="d-flex align-items-center">
                <span class="badge badge-danger px-2 py-1 mr-2 font-size-11 font-weight-bold">
                  {{ item.codigo }}
                </span>
                <h6 class="mb-0 text-danger font-weight-bold font-size-14">
                  {{ item.titulo }}
                </h6>
              </div>
            </div>

            <!-- Corpo do Card -->
            <div class="card-body p-3">
              <p class="text-muted font-size-13">
                {{ item.descricaoResumida }}
              </p>

              <!-- Comparativo de Valores em Métricas Modernas -->
              <div class="metricas-box p-3 rounded">
                <h6 class="font-size-12 text-uppercase text-muted font-weight-bold">
                  <i class="bx bx-calculator mr-1"></i> Valores Utilizados na Análise
                </h6>

                <!-- Comparativo NFS-e vs DAS-D -->
                <div v-if="item.tipoCampos === 'comparativo'" class="row">
                  <div class="col-sm-4 mb-2 mb-sm-0">
                    <div class="card bg-white border shadow-none mb-0 p-2 text-center rounded">
                      <span class="text-muted font-size-11 text-truncate font-weight-medium">Base de Cálculo NFS-e</span>
                      <strong class="font-size-14 text-primary mt-1">
                        {{ (item.dados.esr_base_nfs || 0) | formatNumeroBr }}
                      </strong>
                    </div>
                  </div>
                  <div class="col-sm-4 mb-2 mb-sm-0">
                    <div class="card bg-white border shadow-none mb-0 p-2 text-center rounded">
                      <span class="text-muted font-size-11 text-truncate font-weight-medium">Base de Cálculo DAS-D</span>
                      <strong class="font-size-14 text-dark mt-1">
                        {{ (item.dados.esr_base_das || 0) | formatNumeroBr }}
                      </strong>
                    </div>
                  </div>
                  <div class="col-sm-4">
                    <div class="card bg-soft-danger border-0 shadow-none mb-0 p-2 text-center rounded">
                      <span class="text-danger font-size-11 text-truncate font-weight-semibold">Diferença</span>
                      <strong class="font-size-14 text-danger mt-1">
                        {{ calcularDiferenca(item.dados.esr_base_nfs, item.dados.esr_base_das) | formatNumeroBr }}
                      </strong>
                    </div>
                  </div>
                </div>

                <!-- Somente NFS-e (Tipo 92) -->
                <div v-else-if="item.tipoCampos === 'somente_nfs'" class="row">
                  <div class="col-sm-12">
                    <div class="card bg-white border shadow-none mb-0 p-2 text-center rounded">
                      <span class="text-muted font-size-12 font-weight-medium">Base de Cálculo NFS-e Emitidas (Sem DAS-D)</span>
                      <strong class="font-size-16 text-danger mt-1">
                        {{ (item.dados.esr_base_nfs || 0) | formatNumeroBr }}
                      </strong>
                    </div>
                  </div>
                </div>

                <!-- Somente DAS-D (Tipo 94) -->
                <div v-else-if="item.tipoCampos === 'somente_das'" class="row">
                  <div class="col-sm-6 mb-2 mb-sm-0">
                    <div class="card bg-white border shadow-none mb-0 p-2 text-center rounded">
                      <span class="text-muted font-size-11 font-weight-medium">Base de Cálculo NFS-e Emitidas</span>
                      <strong class="font-size-14 text-dark mt-1">
                        {{ item.dados.esr_das || 'Não informado' }}
                      </strong>
                    </div>
                  </div>
                  <div class="col-sm-6">
                    <div class="card bg-white border shadow-none mb-0 p-2 text-center rounded">
                      <span class="text-muted font-size-11 font-weight-medium">Total de Receitas no DAS-D</span>
                      <strong class="font-size-14 text-danger mt-1">
                        {{ (item.dados.esr_base_das || 0) | formatNumeroBr }}
                      </strong>
                    </div>
                  </div>
                </div>

                <!-- Cadastro + DAS-D (Tipo 95) -->
                <div v-else-if="item.tipoCampos === 'cadastro_das'" class="row">
                  <div class="col-sm-4 mb-2 mb-sm-0">
                    <div class="card bg-white border shadow-none mb-0 p-2 text-center rounded">
                      <span class="text-muted font-size-11 font-weight-medium">Declaração DAS-D</span>
                      <strong class="font-size-13 text-dark mt-1">
                        {{ item.dados.esr_das || 'N/A' }}
                      </strong>
                    </div>
                  </div>
                  <div class="col-sm-4 mb-2 mb-sm-0">
                    <div class="card bg-white border shadow-none mb-0 p-2 text-center rounded">
                      <span class="text-muted font-size-11 font-weight-medium">Base DAS-D</span>
                      <strong class="font-size-13 text-dark mt-1">
                        {{ (item.dados.esr_base_das || 0) | formatNumeroBr }}
                      </strong>
                    </div>
                  </div>
                  <div class="col-sm-4">
                    <div class="card bg-white border shadow-none mb-0 p-2 text-center rounded">
                      <span class="text-muted font-size-11 font-weight-medium">CNPJ Matriz</span>
                      <strong class="font-size-13 text-primary mt-1">
                        {{ item.dados.cnpj ? (item.dados.cnpj | cpfCnpj) : 'N/A' }}
                      </strong>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Seção de Critérios -->
              <div class="mb-1">
                <h6 class="font-size-13 font-weight-bold text-dark mb-2">
                  <i class="bx bx-list-check text-primary mr-1"></i> Critérios para a Geração da Divergência:
                </h6>
                <ul class="criterios-list pl-0 mb-0">
                  <li v-for="(crit, idx) in item.criterios" :key="idx" class="d-flex align-items-start mb-1 font-size-12 text-muted">
                    <i class="bx bx-check text-success font-size-15 mr-2 mt-1"></i>
                    <span>{{ crit }}</span>
                  </li>
                </ul>
              </div>

              <!-- Seção Como Regularizar -->
              <div class="regularizacao-box p-3 rounded">
                <h6 class="font-size-13 font-weight-bold text-danger mb-2">
                  <i class="bx bx-wrench mr-1"></i> Como Regularizar esta Divergência?
                </h6>
                <ul class="regularizacao-list pl-0 mb-0 font-size-12 text-dark">
                  <li v-for="(reg, idx) in item.regularizacao" :key="idx" class="d-flex align-items-start mb-2">
                    <i class="bx bx-right-arrow-alt text-danger font-size-16 mr-1 mt-0"></i>
                    <span>{{ reg }}</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <!-- Coluna Direita: Painel de Análises Realizadas (Checklist) -->
        <div class="col-lg-5">
          <div class="card border-0 shadow-sm sticky-top" style="top: 15px;">
            <div class="card-header bg-white border-bottom py-3">
              <div class="d-flex align-items-center justify-content-between">
                <h6 class="card-title font-size-14 mb-0 font-weight-bold">
                  <i class="bx bx-task text-primary mr-1"></i> Análises Realizadas
                </h6>
                <span class="badge badge-light font-size-11">
                  {{ competencia.slice(4, 6) | monthNames }}/{{ competencia.slice(0, 4) }}
                </span>
              </div>
            </div>
            <div class="card-body p-2">
              <div class="list-group list-group-flush">
                <div
                  v-for="analise in listaAnalises"
                  :key="analise.key"
                  class="list-group-item list-group-item-action border-0 rounded p-2 mb-1 analise-item"
                  :class="{'analise-divergente': analise.divergencia, 'analise-conforme': !analise.divergencia}"
                  @click="focarDivergencia(analise.key)"
                  :style="{ cursor: analise.divergencia ? 'pointer' : 'default' }"
                >
                  <div class="d-flex align-items-start">
                    <div class="mr-2 mt-1">
                      <i
                        v-if="analise.divergencia"
                        class="bx bxs-right-arrow-circle text-danger font-size-18 bx-fade-right"
                      ></i>
                      <i
                        v-else
                        class="bx bxs-check-circle text-success font-size-18"
                      ></i>
                    </div>
                    <div class="flex-grow-1">
                      <div class="d-flex align-items-center justify-content-between">
                        <span class="font-size-11 font-weight-bold" :class="analise.divergencia ? 'text-danger' : 'text-muted'">
                          {{ analise.codigo }}
                        </span>
                        <span
                          class="badge font-size-10 px-2 py-1"
                          :class="analise.divergencia ? 'badge-danger' : 'badge-soft-success'"
                        >
                          {{ analise.divergencia ? 'Divergência' : 'Conforme' }}
                        </span>
                      </div>
                      <h6 class="font-size-12 mb-1 mt-1 font-weight-semibold" :class="analise.divergencia ? 'text-dark' : 'text-muted'">
                        {{ analise.titulo }}
                      </h6>
                      <p class="font-size-11 text-muted mb-0 line-clamp-2">
                        {{ analise.descricaoResumida }}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Loader>
</template>

<script>
import Loader from "../../../../../components/widgets/loader";
import {listApuracaoSimplesNacional} from "../../../../../api/simples_nacional/apuracao";

const ANALISES_CONFIG = [
  {
    key: 'tipo1',
    codigo: 'Tipo 1',
    titulo: 'Base de Cálculo DAS-D ISS Próprio diferente NFS-e (Tributável no Município)',
    descricaoResumida: 'A divergência é gerada somente se a base de cálculo declarada no DAS-D for menor que a base constante nas NFS-e emitidas sem retenção com ISS devido no município.',
    criterios: [
      'O contribuinte deve pertencer ao regime de tributação do Simples Nacional.',
      'Deve existir no DAS-D valores de base de cálculo de ISS Próprio devido no município.',
      'Deve(m) existir NFS-e emitidas sem retenção cujo local de exigibilidade do imposto seja no município.',
      'A divergência é gerada somente se a base de cálculo declarada no DAS-D for menor que a base de cálculo constante nas NFS-e emitidas sem retenção cujo local de exigibilidade seja no município.'
    ],
    regularizacao: [
      'Para resolver a divergência, realize a retificação no DAS-D do valor da base de cálculo declarado na atividade de Prestação de Serviços sem retenção/substituição com ISS devido no Município referente à competência.',
      'Se o valor constante no DAS-D estiver correto, analise o somatório da base de cálculo das notas fiscais de serviços de ISS Próprio da competência e realize, se for o caso, a emissão, cancelamento ou substituição da NFS-e.',
      'A conferência das bases de cálculo é feita pela data de competência do fato gerador da nota fiscal, nunca pela data de emissão.',
      'Sempre será considerado o valor da Base de Cálculo do ISS acrescido dos Valores das Deduções permitidas informadas no local apropriado no DAS-D.'
    ],
    tipoCampos: 'comparativo'
  },
  {
    key: 'tipo2',
    codigo: 'Tipo 2',
    titulo: 'Base de Cálculo DAS-D diferente da NFS-e (Outros Municípios)',
    descricaoResumida: 'A divergência é gerada somente se a base constante nas NFS-e sem retenção devidas fora do município for menor que a base declarada no DAS-D.',
    criterios: [
      'O contribuinte deve pertencer ao regime de tributação do Simples Nacional.',
      'Deve existir no DAS-D valores de base de cálculo de ISS em outro município.',
      'Deve(m) existir NFS-e emitidas sem retenção cujo local de exigibilidade do imposto seja fora do município.',
      'A divergência é gerada somente se a base constante nas NFS-e emitidas sem retenção fora do município for menor que a declarada no DAS-D.'
    ],
    regularizacao: [
      'Realizar a retificação no DAS-D do valor da base de cálculo declarado na atividade de Prestação de Serviços sem retenção/substituição com ISS devido a outro(s) município(s).',
      'Caso o valor no DAS-D esteja correto, revisar as notas fiscais emitidas para outros municípios e efetuar as correções necessárias.',
      'A conferência das bases é realizada pela data de competência do fato gerador.'
    ],
    tipoCampos: 'comparativo'
  },
  {
    key: 'tipo3',
    codigo: 'Tipo 3',
    titulo: 'Base de Cálculo DAS-D diferente da NFS-e (ISS Retido)',
    descricaoResumida: 'A divergência é gerada somente se a base constante nas NFS-e emitidas com retenção do ISS na fonte for menor que a declarada no DAS-D.',
    criterios: [
      'O contribuinte deve pertencer ao regime de tributação do Simples Nacional.',
      'Deve existir no DAS-D valores de base de cálculo de ISS com retenção/substituição tributária.',
      'Deve(m) existir NFS-e emitidas com retenção/substituição de ISS.',
      'A divergência ocorre quando a base nas NFS-e com retenção do ISS for menor que a declarada no DAS-D.'
    ],
    regularizacao: [
      'Realizar a retificação no DAS-D do valor da base de cálculo declarado com retenção/substituição do ISS referente à competência.',
      'Se o valor no DAS-D estiver correto, verificar as notas com retenção na fonte e realizar emissão/cancelamento/substituição se necessário.',
      'Não há possibilidade de declaração com redução do fato gerador do ISS nesta categoria.'
    ],
    tipoCampos: 'comparativo'
  },
  {
    key: 'tipo4',
    codigo: 'Tipo 4',
    titulo: 'Base de Cálculo DAS-D diferente da NFS-e (Não sujeito a ISS sobre o faturamento)',
    descricaoResumida: 'A divergência ocorre quando a base nas NFS-e não incidentes de ISS for maior que a base declarada no DAS-D.',
    criterios: [
      'O contribuinte deve pertencer ao regime de tributação do Simples Nacional.',
      'Deve existir no DAS-D valores de Locação de Bens Móveis ou Serviços contábeis com ISS Fixo devido ao município.',
      'Deve(m) existir NFS-e emitidas com exigibilidade de "não incidência" e/ou Regime Especial com indicação de "Sociedade de Profissionais".',
      'A divergência ocorre quando a base nas NFS-e não incidentes (Grupo 99.XX / LC116 17.19) for maior que a declarada no DAS-D.'
    ],
    regularizacao: [
      'Realizar a retificação no DAS-D do valor da base declarado na atividade de Locação de Bens e/ou Serviços contábeis.',
      'Se o valor do DAS-D estiver correto, analisar o somatório das NFS-e emitidas no grupo não sujeito a ISS sobre o faturamento.'
    ],
    tipoCampos: 'comparativo'
  },
  {
    key: 'tipo92',
    codigo: 'Tipo 92',
    titulo: 'Optantes sem DAS-D e com emissão de NFS-e',
    descricaoResumida: 'A divergência ocorre quando não há declaração DAS-D transmitida no Simples Nacional, havendo notas fiscais emitidas válidas.',
    criterios: [
      'O contribuinte deve pertencer ao regime de tributação do Simples Nacional.',
      'Não foi localizada declaração DAS-D no período da apuração.',
      'Existem NFS-e emitidas válidas (não canceladas) no período de apuração.',
      'Sujeito a penalidade por ausência de transmissão da declaração.'
    ],
    regularizacao: [
      'Transmitir a declaração do DAS-D no portal do Simples Nacional com os valores de base de cálculo correspondentes às NFS-e emitidas na competência.'
    ],
    tipoCampos: 'somente_nfs'
  },
  {
    key: 'tipo94',
    codigo: 'Tipo 94',
    titulo: 'Optantes com DAS-D e sem emissão de NFS-e',
    descricaoResumida: 'A divergência ocorre quando há declaração no DAS-D com receitas de serviços ou locação, porém sem nenhuma NFS-e emitida.',
    criterios: [
      'O contribuinte deve pertencer ao regime de tributação do Simples Nacional e estar autorizado a emitir notas.',
      'Possui declaração DAS-D com receitas de serviços ou locação de bens no período.',
      'Não possui NFS-e emitidas no período da apuração.',
      'Sujeito a penalidade por ausência de documento fiscal obrigatório.'
    ],
    regularizacao: [
      'Realizar a emissão das NFS-e da competência de acordo com os valores declarados no DAS-D.',
      'Qualquer redução indevida no DAS-D está sujeita a malha fiscal da Receita Federal, Estado e Município.'
    ],
    tipoCampos: 'somente_das'
  },
  {
    key: 'tipo95',
    codigo: 'Tipo 95',
    titulo: 'Optantes com DAS-D e sem cadastro no NFS-e',
    descricaoResumida: 'A divergência ocorre quando há DAS-D transmitido, porém o contribuinte não possui cadastro econômico ativo no Sistema de NFS-e.',
    criterios: [
      'O contribuinte deve pertencer ao regime de tributação do Simples Nacional.',
      'Possui declaração DAS-D com serviços ou locação de bens no período.',
      'Não possui cadastro econômico (CEC) ativo no Sistema de NFS-e até o período da apuração.',
      'Passível de penalidade por ausência de cadastro mobiliário e licença de funcionamento.'
    ],
    regularizacao: [
      'Providenciar a abertura do Cadastro Econômico do Contribuinte (CEC) junto ao setor de cadastros da Prefeitura para regularização.'
    ],
    tipoCampos: 'cadastro_das'
  }
];

export default {
  name: "simplesApuracao",
  components: {
    Loader,
  },
  props: {
    eco_codigo: {
      type: String,
      required: true,
    },
    competencia: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      httpSend: false,
      sync: false,
      apuracoes: {}
    };
  },
  computed: {
    listaAnalises() {
      return ANALISES_CONFIG.map(cfg => {
        const dados = (this.apuracoes && this.apuracoes[cfg.key]) || {};
        return {
          ...cfg,
          dados,
          divergencia: Boolean(dados.divergencia)
        };
      });
    },
    listaDivergentes() {
      return this.listaAnalises.filter(a => a.divergencia);
    },
    totalDivergencias() {
      return this.listaDivergentes.length;
    },
    totalConformes() {
      return this.listaAnalises.length - this.totalDivergencias;
    }
  },
  created() {
    this.list_apuracao();
  },
  methods: {
    list_apuracao() {
      this.httpSend = true;
      listApuracaoSimplesNacional(this.eco_codigo, this.competencia).then(result => {
        const dados = result.dados;
        this.sync = dados.sync;
        if (dados.sync) {
          this.apuracoes = dados.apuracao || {};
        } else {
          this.apuracoes = {};
        }
      }).catch(error => {
        console.log(error);
      }).finally(() => {
        this.httpSend = false;
      });
    },
    calcularDiferenca(nfs, das) {
      const vNfs = Number(nfs) || 0;
      const vDas = Number(das) || 0;
      return Math.abs(vNfs - vDas);
    },
    focarDivergencia(key) {
      const el = document.getElementById('card-' + key);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.classList.add('card-highlight');
        setTimeout(() => {
          el.classList.remove('card-highlight');
        }, 1800);
      }
    }
  }
};
</script>

<style scoped>
.status-banner {
  border-radius: 8px;
}

.status-danger {
  border-left: 4px solid #f46a6a !important;
  background: #fff5f5;
}

.status-success {
  border-left: 4px solid #34c38f !important;
  background: #f0fbf7;
}

.card-divergencia {
  border-left: 4px solid #f46a6a !important;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.card-highlight {
  box-shadow: 0 0 0 3px rgba(244, 106, 106, 0.4), 0 8px 16px rgba(244, 106, 106, 0.2) !important;
  transform: translateY(-2px);
}

.metricas-box {
  background-color: #f8f9fa;
  border: 1px solid #edf2f7;
}

.criterios-list li {
  line-height: 1.5;
}

.regularizacao-box {
  background-color: #fef4f4;
  border-left: 3px solid #f46a6a;
}

.regularizacao-list li {
  line-height: 1.5;
}

.analise-item {
  transition: all 0.2s ease;
}

.analise-item:hover {
  background-color: #f8f9fa;
}

.analise-divergente {
  background-color: #fff9f9;
}

.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.bg-soft-danger {
  background-color: rgba(244, 106, 106, 0.15) !important;
}

.bg-soft-success {
  background-color: rgba(52, 195, 143, 0.15) !important;
}

.bg-soft-info {
  background-color: rgba(80, 165, 241, 0.15) !important;
}

.badge-soft-danger {
  color: #f46a6a;
  background-color: rgba(244, 106, 106, 0.15);
}

.badge-soft-success {
  color: #34c38f;
  background-color: rgba(52, 195, 143, 0.15);
}

.badge-soft-info {
  color: #50a5f1;
  background-color: rgba(80, 165, 241, 0.15);
}
</style>
